/**
 * The `Options` column, read (13-10). `Options` is free Note text written by the cards and the
 * sweep, for example:
 *
 *   "(a) Remediate this client only. (b) Remediate every client billed the same way. Agent view:
 *    (b) closes the loop and is discoverable anyway; confidence 65%. Human-only - CEO and Ops Manager."
 *   "A: keep the current format. B: move to a shorter format. Recommendation: B (confidence 0.7)."
 *
 * `parseOptions(text)` lays that out as choices plus the writer's recommendation, so a Decision
 * card can put the recommendation first (D-13-02-1) and the regulatory panel can list the choices
 * neutrally (D-13-00-5). Recognised:
 *   - lists: `(a) … (b) …`, `a) … b) …`, `A: … B: …`, `1. … 2. …`, `1) … 2) …` (in sequence from
 *     a / 1, at least two items);
 *   - a two-way `X or Y` with no list markers;
 *   - the markers `Agent view:`, `Recommendation:`, `Recommended:`, `Suggested:`, then a key;
 *   - `confidence NN%`, `confidence 0.NN`, `NN% confidence`.
 *
 * **It never invents a recommendation.** `recommended` is set only when a marker names a key that
 * is one of the parsed options. When no list can be read, `options` is `[]` and the caller shows
 * the raw text with a free-text "Your decision" field.
 *
 * Also here, because they read option text: the value written to `DecidedOption` (a 255-character
 * Text column) and the "the incident record says otherwise" check for the regulatory panel.
 *
 * Pure: no React, no I/O.
 */

export interface ParsedOption {
  /** Lower-case letter or number, as written (`a`, `b`, `1`). */
  key: string;
  /** The option's text, trimmed, its trailing full stop or semicolon dropped. */
  label: string;
}

export interface ParsedOptions {
  /** In the order written. Empty when no list of two or more could be read. */
  options: ParsedOption[];
  /** The key the writer recommended, only when it names one of `options`. */
  recommended?: string;
  /** 0–100, only when stated. */
  confidence?: number;
  /** The writer's reason for the recommendation, as written. */
  reasoning?: string;
  /** Text after the recommendation that is neither its reason nor its confidence. */
  remark?: string;
  /** The text as stored. */
  raw: string;
}

// ---------------------------------------------------------------------------
// Markers
// ---------------------------------------------------------------------------

/** Where the recommendation starts. The first one wins. */
const RECOMMENDATION_MARKER = /\b(?:agent view|recommendation|recommended|suggested)\s*[:–—-]\s*/i;

/**
 * A key right after the marker: `(b)`, `b)`, `B`, `2`. A bare letter must be followed by
 * punctuation or the end, so "Recommended: a shorter format" is never read as option (a).
 */
const LEADING_KEY = /^(?:\(\s*([a-z]|\d{1,2})\s*\)|([a-z]|\d{1,2})\s*\)|([a-z]|\d{1,2})(?=\s*(?:[.,;:(]|$)))/i;

const CONFIDENCE_PATTERNS: readonly RegExp[] = [
  /\(?\s*confidence\s*(?:of|is|=|:)?\s*(\d{1,3}(?:\.\d+)?)\s*(%)?\s*\)?/i,
  /\(?\s*(\d{1,3})\s*(%)\s*confidence\s*\)?/i,
];

interface Marker {
  key: string;
  /** Where the marker starts. */
  start: number;
  /** Where its label starts. */
  end: number;
}

type ListStyle = 'paren' | 'letterParen' | 'letterColon' | 'numberDot' | 'numberParen';

/** Candidate markers per list style; each match's `key` group is the key. */
const LIST_PATTERNS: Record<ListStyle, RegExp> = {
  paren: /\(\s*([a-z])\s*\)\s*/gi,
  letterParen: /(?:^|(?<=[\s.;:]))([a-z])\)\s+/gi,
  letterColon: /(?:^|(?<=[\s.;]))([a-z]):\s+/gi,
  numberDot: /(?:^|(?<=[\s;:]))(\d{1,2})\.\s+/g,
  numberParen: /(?:^|(?<=[\s.;:]))(\d{1,2})\)\s+/g,
};

function nextKey(key: string): string {
  if (/^\d+$/.test(key)) return String(Number(key) + 1);
  return String.fromCharCode(key.charCodeAt(0) + 1);
}

/** The longest in-sequence run from `a` / `1`, taking the first match of each next key. */
function sequenceOf(text: string, pattern: RegExp): Marker[] {
  const found: Marker[] = [];
  for (const m of text.matchAll(pattern)) {
    found.push({ key: m[1].toLowerCase(), start: m.index ?? 0, end: (m.index ?? 0) + m[0].length });
  }
  const run: Marker[] = [];
  let want = /^\d/.test(found[0]?.key ?? '') ? '1' : 'a';
  for (const marker of found) {
    if (marker.key !== want) continue;
    if (run.length && marker.start < run[run.length - 1].end) continue;
    run.push(marker);
    want = nextKey(want);
  }
  return run;
}

function cleanLabel(text: string): string {
  let t = text.replace(/\s+/g, ' ').trim().replace(/^[,;:–—-]\s*/, '');
  // Drop the joining tail of a list item: "X; or", "X, or", "X.".
  for (let prev = ''; prev !== t; ) {
    prev = t;
    t = t.replace(/(?:\s+or|[.;,])\s*$/i, '').trim();
  }
  return t;
}

function listFrom(body: string): ParsedOption[] {
  let best: Marker[] = [];
  for (const style of Object.keys(LIST_PATTERNS) as ListStyle[]) {
    const run = sequenceOf(body, LIST_PATTERNS[style]);
    if (run.length > best.length) best = run;
  }
  if (best.length < 2) return [];
  const options = best.map((marker, i) => ({
    key: marker.key,
    label: cleanLabel(body.slice(marker.end, i + 1 < best.length ? best[i + 1].start : body.length)),
  }));
  return options.every((o) => o.label) ? options : [];
}

/** "Keep 9 October or move the review to 20 October?": exactly one " or ", one sentence. */
function twoWayFrom(body: string): ParsedOption[] {
  const text = body.replace(/\s+/g, ' ').trim().replace(/[?.]$/, '');
  if (!text || /[.?!;]\s/.test(text)) return [];
  const parts = text.split(/,?\s+or\s+/i);
  if (parts.length !== 2) return [];
  const first = parts[0].replace(/^(?:either|whether to|whether)\s+/i, '');
  const options = [
    { key: 'a', label: cleanLabel(first) },
    { key: 'b', label: cleanLabel(parts[1]) },
  ];
  return options.every((o) => o.label) ? options : [];
}

function confidenceIn(text: string): { value: number; match: RegExpExecArray } | null {
  for (const pattern of CONFIDENCE_PATTERNS) {
    const m = pattern.exec(text);
    if (!m) continue;
    const n = Number(m[1]);
    if (!Number.isFinite(n)) continue;
    const value = m[2] === '%' ? n : n <= 1 ? n * 100 : n;
    if (value < 0 || value > 100) continue;
    return { value: Math.round(value), match: m };
  }
  return null;
}

function tidy(text: string): string | undefined {
  const t = text
    .replace(/\s+/g, ' ')
    .replace(/\(\s*\)/g, '')
    .trim()
    .replace(/^[,;:.–—-]+\s*/, '')
    .replace(/\s*[,;:.–—-]+$/, '')
    .trim();
  return t ? t : undefined;
}

// ---------------------------------------------------------------------------
// parseOptions
// ---------------------------------------------------------------------------

export function parseOptions(text: string | null | undefined): ParsedOptions {
  const raw = text ?? '';
  const flat = raw.replace(/\r\n?/g, '\n');
  const marker = RECOMMENDATION_MARKER.exec(flat);
  const body = marker ? flat.slice(0, marker.index) : flat;
  const tail = marker ? flat.slice(marker.index + marker[0].length) : '';

  const options = listFrom(body);
  const parsed: ParsedOptions = { options: options.length ? options : twoWayFrom(body), raw };
  if (parsed.options.length === 0 || !marker) return parsed;

  // The recommendation: a key the list really has, then its reason, confidence and any remark.
  const lead = tail.trimStart();
  const keyMatch = LEADING_KEY.exec(lead);
  if (!keyMatch) return parsed;
  const key = (keyMatch[1] ?? keyMatch[2] ?? keyMatch[3]).toLowerCase();
  if (!parsed.options.some((o) => o.key === key)) return parsed;
  parsed.recommended = key;

  const afterKey = lead.slice(keyMatch[0].length);
  const conf = confidenceIn(afterKey);
  if (conf) {
    parsed.confidence = conf.value;
    const reasoning = tidy(afterKey.slice(0, conf.match.index));
    const remark = tidy(afterKey.slice(conf.match.index + conf.match[0].length));
    if (reasoning) parsed.reasoning = reasoning;
    if (remark) parsed.remark = remark;
  } else {
    const reasoning = tidy(afterKey);
    if (reasoning) parsed.reasoning = reasoning;
  }
  return parsed;
}

/**
 * An option's words for a button or heading: the first letter capitalised ("keep both" →
 * "Keep both"). Display only; `decidedOptionValue` keeps the text as written.
 */
export function optionText(label: string): string {
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : label;
}

/** The option a key names, or undefined. */
export function optionFor(parsed: ParsedOptions, key: string | undefined): ParsedOption | undefined {
  return key === undefined ? undefined : parsed.options.find((o) => o.key === key);
}

// ---------------------------------------------------------------------------
// DecidedOption (a Text column: 255 characters)
// ---------------------------------------------------------------------------

/** `DecidedOption` is a SharePoint Text column, 255 characters (provision_action_register.py). */
export const DECIDED_OPTION_MAX = 255;

/**
 * What `decide` writes to `DecidedOption`: `(b) <label>` for a listed option, or the free text.
 * Cut to 255 characters with an ellipsis: an over-long Text value fails the whole PATCH, and the
 * full wording stays in `Options` (and the key says which one it was).
 */
export function decidedOptionValue(choice: ParsedOption | string): string {
  const text = typeof choice === 'string' ? choice.replace(/\s+/g, ' ').trim() : `(${choice.key}) ${choice.label}`;
  if (text.length <= DECIDED_OPTION_MAX) return text;
  return `${text.slice(0, DECIDED_OPTION_MAX - 1).trimEnd()}…`;
}

// ---------------------------------------------------------------------------
// The parent Incident's own decision (regulatory panel)
// ---------------------------------------------------------------------------

/** The live `ReportabilityDecision` choices (extend_incidents_for_card.py, 27 Sep 2026). */
export const REPORTABILITY_DECISIONS = [
  'Pending',
  'NotReportable',
  'SIRS-P1',
  'SIRS-P2',
  'NDIS-Reportable',
  'WorkSafe',
  'Other',
] as const;

export type ReportabilityDecision = (typeof REPORTABILITY_DECISIONS)[number];

const DECISION_WORDS: Record<ReportabilityDecision, string> = {
  Pending: 'Pending',
  NotReportable: 'Not reportable',
  'SIRS-P1': 'SIRS Priority 1',
  'SIRS-P2': 'SIRS Priority 2',
  'NDIS-Reportable': 'Reportable to the NDIS Commission',
  WorkSafe: 'Notifiable to WorkSafe',
  Other: 'Other',
};

/** Plain words for a `ReportabilityDecision` value; an unknown value is shown as written. */
export function reportabilityWords(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (!v) return 'no decision yet';
  return (DECISION_WORDS as Record<string, string>)[v] ?? v;
}

type Stance = 'reportable' | 'not-reportable';

/** What an option's words say about reportability, or null when they don't say. */
export function optionStance(label: string): { stance: Stance; priority?: 'SIRS-P1' | 'SIRS-P2' } | null {
  const t = label.toLowerCase();
  if (/\bnot\s+(?:reportable|notifiable|a\s+serious\s+injury)\b|\bno\s+report(?:ing)?\b|\bnot\s+report\b/.test(t)) {
    return { stance: 'not-reportable' };
  }
  if (/\breportable\b|\bnotif(?:y|iable)\b|\bserious\s+injury\b|\bsirs\b|\bworksafe\b|\bcommission\b/.test(t)) {
    const priority = /\bpriority\s*1\b|\bp1\b/.test(t) ? 'SIRS-P1' : /\bpriority\s*2\b|\bp2\b/.test(t) ? 'SIRS-P2' : undefined;
    return priority ? { stance: 'reportable', priority } : { stance: 'reportable' };
  }
  return null;
}

function decisionStance(value: string): Stance | 'pending' | null {
  switch (value) {
    case '':
    case 'Pending':
      return 'pending';
    case 'NotReportable':
      return 'not-reportable';
    case 'SIRS-P1':
    case 'SIRS-P2':
    case 'NDIS-Reportable':
    case 'WorkSafe':
      return 'reportable';
    default:
      return null; // Other, or a value this build doesn't know: say nothing rather than guess
  }
}

/**
 * The non-blocking note when the parent Incident's `ReportabilityDecision` disagrees with the
 * chosen option, or null. The panel never writes the Incident (cross-register writes are out of
 * scope; CARD-QS-001 step 4 is the human path), so it only says so.
 */
export function incidentDisagreement(chosenLabel: string, incidentDecision: string | null | undefined): string | null {
  const option = optionStance(chosenLabel);
  if (!option) return null;
  const value = (incidentDecision ?? '').trim();
  const incident = decisionStance(value);
  if (incident === null) return null;
  const differs =
    incident !== option.stance ||
    (option.priority !== undefined && (value === 'SIRS-P1' || value === 'SIRS-P2') && value !== option.priority);
  if (!differs) return null;
  return value === ''
    ? 'The incident record has no reportability decision yet. Update it on the incident too.'
    : `The incident record says ${reportabilityWords(value)}. Update it on the incident too.`;
}
